// RiskManagementPage.jsx - Only height adjusted to fill screen, elements unchanged
import React, { useState, useEffect, useMemo } from "react";
import {
  MdWarning,
  MdCheckCircle,
  MdBlock,
  MdVerifiedUser,
  MdOutlineSecurity,
  MdReceiptLong,
  MdSettings,
  MdClose,
  MdAccessTime,
  MdAttachMoney,
  MdLocationOn,
  MdDevices,
  MdPerson,
  MdShoppingBag,
  MdPayment,
  MdLocalShipping,
  MdRefresh,
  MdInfo,
  MdAnalytics,
  MdAdd,
  MdNote,
  MdSave,
  MdDelete,
  MdCheck,
  MdEdit,
  MdArrowBack,
} from "react-icons/md";
import { useParams } from "react-router-dom";
import { getAllOrders, updateOrder } from "../../services/order.service";
import {
  getRiskSettings,
  updateRiskSettings,
  resetRiskSettings,
  updatePincodeBlockList,
} from "../../services/riskSettings.service";
import useTheme from "../../constants/there";
import { toast } from "react-toastify";
import { LogoLoading } from "../../components/common/utlity/utility.js";
import { useTranslation } from "react-i18next";

// Risk level colors (unchanged)
const riskLevelColors = {
  low: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
  acceptable: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
  medium: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300",
  high: "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300",
  very_high: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
  critical: "bg-red-200 text-red-900 dark:bg-red-950 dark:text-red-200",
};

// Light background colors (unchanged)
const categoryLightColors = {
  low: "bg-green-50 hover:bg-green-100 border-green-200 text-green-800",
  acceptable: "bg-blue-50 hover:bg-blue-100 border-blue-200 text-blue-800",
  medium: "bg-yellow-50 hover:bg-yellow-100 border-yellow-200 text-yellow-800",
  high: "bg-orange-50 hover:bg-orange-100 border-orange-200 text-orange-800",
  very_high: "bg-red-50 hover:bg-red-100 border-red-200 text-red-800",
  critical: "bg-red-100 hover:bg-red-200 border-red-300 text-red-900",
};

export default function RiskManagementPage() {
  const { t } = useTranslation();
  const [orders, setOrders] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isTableOpen, setIsTableOpen] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);
  const [loading, setLoading] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [selectedOrderForNote, setSelectedOrderForNote] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [orderNotes, setOrderNotes] = useState({});
  const [updatingOrderId, setUpdatingOrderId] = useState(null);
  const { bussinessUuid } = useParams();
  const { themeMode } = useTheme();
  const isDarkMode =
    themeMode === "dark" ||
    (themeMode !== "light" && document.documentElement.classList.contains("dark"));

  // Action labels (unchanged)
  const actionLabels = {
    approve: { label: t('RISK_MANAGEMENT.APPROVE', 'Approve'), icon: <MdCheckCircle className="text-green-500" /> },
    approve_with_log: { label: t('RISK_MANAGEMENT.APPROVE_WITH_LOG', 'Approve + Monitor'), icon: <MdVerifiedUser className="text-blue-500" /> },
    otp_verify: { label: t('RISK_MANAGEMENT.OTP_VERIFY', 'OTP Verify'), icon: <MdOutlineSecurity className="text-yellow-500" /> },
    manual_review: { label: t('RISK_MANAGEMENT.MANUAL_REVIEW', 'Manual Review'), icon: <MdWarning className="text-orange-500" /> },
    block: { label: t('RISK_MANAGEMENT.BLOCK', 'Block'), icon: <MdBlock className="text-red-500" /> },
  };

  // Risk categories (unchanged)
  const riskCategories = [
    { id: "low", label: t('RISK_MANAGEMENT.LOW_RISK', 'Low Risk'), icon: <MdCheckCircle />, color: "text-green-500", bgColor: "bg-green-500" },
    { id: "acceptable", label: t('RISK_MANAGEMENT.ACCEPTABLE_RISK', 'Acceptable Risk'), icon: <MdVerifiedUser />, color: "text-blue-500", bgColor: "bg-blue-500" },
    { id: "medium", label: t('RISK_MANAGEMENT.MEDIUM_RISK', 'Medium Risk'), icon: <MdWarning />, color: "text-yellow-500", bgColor: "bg-yellow-500" },
    { id: "high", label: t('RISK_MANAGEMENT.HIGH_RISK', 'High Risk'), icon: <MdWarning />, color: "text-orange-500", bgColor: "bg-orange-500" },
    { id: "very_high", label: t('RISK_MANAGEMENT.VERY_HIGH_RISK', 'Very High Risk'), icon: <MdBlock />, color: "text-red-500", bgColor: "bg-red-500" },
    { id: "critical", label: t('RISK_MANAGEMENT.CRITICAL_RISK', 'Critical Risk'), icon: <MdBlock />, color: "text-red-700", bgColor: "bg-red-700" },
  ];

  // Screen size detection (unchanged)
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobileView(window.innerWidth < 1024);
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Settings form state (unchanged)
  const [settingsForm, setSettingsForm] = useState({
    codLimit: 10000,
    prepaidLimit: 25000,
    offTimeStart: "22:00",
    offTimeEnd: "06:00",
    pincodeBlockList: [],
    maxOrdersPerDevice: 3,
    lowRisk: {
      disableCod: false,
      disableReturns: false,
      blockCheckout: false,
      approvalMethod: "approve",
    },
    acceptableRisk: {
      disableCod: false,
      disableReturns: false,
      blockCheckout: false,
      approvalMethod: "approve_with_log",
    },
    mediumRisk: {
      disableCod: false,
      disableReturns: false,
      blockCheckout: false,
      approvalMethod: "otp_verify",
    },
    highRisk: {
      disableCod: true,
      disableReturns: true,
      blockCheckout: false,
      approvalMethod: "manual_review",
    },
    veryHighRisk: {
      disableCod: true,
      disableReturns: true,
      blockCheckout: true,
      approvalMethod: "block",
    },
    criticalRisk: {
      disableCod: true,
      disableReturns: true,
      blockCheckout: true,
      approvalMethod: "block",
    },
    isActive: true,
    autoApproveLowRisk: true,
    notifyOnHighRisk: true,
  });

  // Load settings (unchanged)
  useEffect(() => {
    const loadSettings = async () => {
      if (!bussinessUuid) return;
      try {
        const response = await getRiskSettings(bussinessUuid);
        if (response.success && response.data) {
          setSettingsForm(response.data);
        }
      } catch (error) {
        console.error("Failed to load risk settings:", error);
        toast.warning(t('RISK_MANAGEMENT.DEFAULT_SETTINGS_WARNING', 'Using default risk settings'));
      }
    };
    loadSettings();
  }, [bussinessUuid, t]);

  // Save settings (unchanged)
  const saveSettings = async () => {
    if (!bussinessUuid) return;
    setSavingSettings(true);
    try {
      const response = await updateRiskSettings(bussinessUuid, settingsForm);
      if (response.success) {
        toast.success(t('RISK_MANAGEMENT.SETTINGS_SAVED', 'Risk settings saved successfully!'));
        setShowSettings(false);
      } else {
        toast.error(t('RISK_MANAGEMENT.SETTINGS_SAVE_FAILED', 'Failed to save settings'));
      }
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error(t('RISK_MANAGEMENT.SETTINGS_SAVE_FAILED', 'Failed to save settings'));
    } finally {
      setSavingSettings(false);
    }
  };

  // Reset settings (unchanged)
  const resetSettings = async () => {
    if (!bussinessUuid) return;
    setSavingSettings(true);
    try {
      const response = await resetRiskSettings(bussinessUuid);
      if (response.success) {
        setSettingsForm(response.data);
        toast.success(t('RISK_MANAGEMENT.SETTINGS_RESET', 'Settings reset to default!'));
      } else {
        toast.error(t('RISK_MANAGEMENT.SETTINGS_RESET_FAILED', 'Failed to reset settings'));
      }
    } catch (error) {
      console.error("Error resetting settings:", error);
      toast.error(t('RISK_MANAGEMENT.SETTINGS_RESET_FAILED', 'Failed to reset settings'));
    } finally {
      setSavingSettings(false);
    }
  };

  // Pincode functions (unchanged)
  const addPincode = async (pincode) => {
    if (pincode && pincode.length === 6 && !settingsForm.pincodeBlockList?.includes(pincode)) {
      const newList = [...(settingsForm.pincodeBlockList || []), pincode];
      setSettingsForm(prev => ({
        ...prev,
        pincodeBlockList: newList,
      }));
      try {
        await updatePincodeBlockList(bussinessUuid, [pincode], 'add');
        toast.success(t('RISK_MANAGEMENT.PINCODE_ADDED', 'Pincode added to block list'));
      } catch (error) {
        console.error("Error saving pincode:", error);
        toast.error(t('RISK_MANAGEMENT.PINCODE_SAVE_FAILED', 'Failed to save pincode'));
      }
    }
  };

  const removePincode = async (pincode) => {
    const newList = settingsForm.pincodeBlockList?.filter(p => p !== pincode) || [];
    setSettingsForm(prev => ({
      ...prev,
      pincodeBlockList: newList,
    }));
    try {
      await updatePincodeBlockList(bussinessUuid, [pincode], 'remove');
      toast.success(t('RISK_MANAGEMENT.PINCODE_REMOVED', 'Pincode removed from block list'));
    } catch (error) {
      console.error("Error removing pincode:", error);
      toast.error(t('RISK_MANAGEMENT.PINCODE_REMOVE_FAILED', 'Failed to remove pincode'));
    }
  };

  const updateRiskSetting = (riskLevel, key, value) => {
    setSettingsForm(prev => ({
      ...prev,
      [riskLevel]: {
        ...(prev[riskLevel] || {}),
        [key]: value,
      },
    }));
  };

  // Confirm order (unchanged)
  const handleConfirmOrder = async (order) => {
    if (!order || !order.customerId) {
      toast.error(t('RISK_MANAGEMENT.INVALID_ORDER', 'Invalid order data'));
      return;
    }
    setUpdatingOrderId(order.orderId);
    try {
      const payload = { orderStatus: "Confirmed" };
      const response = await updateOrder(bussinessUuid, order.customerId, order.orderId, payload);
      if (response) {
        toast.success(t('RISK_MANAGEMENT.ORDER_CONFIRMED', { 
          orderId: order.orderId,
          defaultValue: `Order #${order.orderId} has been confirmed!` 
        }));
        await fetchOrders();
      } else {
        toast.error(t('RISK_MANAGEMENT.ORDER_UPDATE_FAILED', 'Failed to update order status'));
      }
    } catch (error) {
      console.error("Error updating order:", error);
      toast.error(error?.response?.data?.message || t('RISK_MANAGEMENT.ORDER_UPDATE_FAILED', 'Failed to update order status'));
    } finally {
      setUpdatingOrderId(null);
    }
  };

  // Helper functions (unchanged)
  const convertReasonsToArray = (reasons) => {
    if (!reasons) return [t('RISK_MANAGEMENT.NO_SPECIFIC_REASONS', 'No specific reasons')];
    if (Array.isArray(reasons)) return reasons;
    if (typeof reasons === 'object') {
      const reasonsArray = [];
      Object.keys(reasons).forEach(key => {
        if (Array.isArray(reasons[key])) reasonsArray.push(...reasons[key]);
        else if (typeof reasons[key] === 'string') reasonsArray.push(reasons[key]);
      });
      return reasonsArray.length > 0 ? reasonsArray : [t('RISK_MANAGEMENT.NO_SPECIFIC_REASONS', 'No specific reasons')];
    }
    return [t('RISK_MANAGEMENT.NO_SPECIFIC_REASONS', 'No specific reasons')];
  };

  const getRiskLevelFromScore = (score) => {
    if (score <= 24) return "low";
    if (score <= 44) return "acceptable";
    if (score <= 59) return "medium";
    if (score <= 74) return "high";
    if (score <= 89) return "very_high";
    return "critical";
  };

  const getActionFromScore = (score) => {
    if (score <= 24) return "approve";
    if (score <= 44) return "approve_with_log";
    if (score <= 59) return "otp_verify";
    if (score <= 74) return "manual_review";
    if (score <= 89) return "block";
    return "block";
  };

  // Fetch orders (unchanged)
  const fetchOrders = async () => {
    if (!bussinessUuid) return;
    setLoading(true);
    try {
      const res = await getAllOrders(bussinessUuid, {});
      const orderData = res?.data?.data || [];
      const processedOrders = orderData.map((order) => {
        let riskLevel = "low";
        let riskScore = 0;
        let recommendedAction = "approve";
        let riskReasons = [];
        const isOtpVerified = 
          order.orderStatus === "Verified" ||
          order.riskAssessment?.otpVerified === true ||
          order.riskOtpVerified === true ||
          (order.riskAssessment?.finalAction === "otp_verify" && order.orderStatus === "Confirmed");
        if (isOtpVerified) return null;
        if (order.riskAssessment) {
          riskLevel = order.riskAssessment.level || "low";
          riskScore = order.riskAssessment.score || 0;
          recommendedAction = order.riskAssessment.finalAction || order.riskAssessment.originalAction || "approve";
          riskReasons = convertReasonsToArray(order.riskAssessment.reasons);
        } else if (order.riskAssessment?.flaskResponse) {
          const flaskData = order.riskAssessment.flaskResponse;
          riskScore = flaskData.final_score || 0;
          riskLevel = flaskData.risk_level || getRiskLevelFromScore(riskScore);
          recommendedAction = flaskData.recommended_action || getActionFromScore(riskScore);
          riskReasons = convertReasonsToArray(flaskData.reasons);
        } else if (order.risk_level || order.risk_score) {
          riskLevel = order.risk_level || "low";
          riskScore = order.risk_score || 0;
          recommendedAction = order.recommended_action || getActionFromScore(riskScore);
          riskReasons = convertReasonsToArray(order.risk_reasons);
        } else {
          const orderTotal = order.total || 0;
          const isCOD = String(order.paymentMethod || "").toLowerCase() === "cod";
          let estimatedScore = 0;
          if (isCOD && orderTotal > 25000) estimatedScore = 70;
          else if (isCOD && orderTotal > 10000) estimatedScore = 55;
          else if (orderTotal > 25000) estimatedScore = 45;
          else if (orderTotal > 10000) estimatedScore = 35;
          else estimatedScore = 20;
          riskScore = estimatedScore;
          riskLevel = getRiskLevelFromScore(estimatedScore);
          recommendedAction = getActionFromScore(estimatedScore);
          riskReasons = [`${t('RISK_MANAGEMENT.ORDER_TOTAL', 'Order total')}: ₹${orderTotal}`, `${t('RISK_MANAGEMENT.PAYMENT_METHOD', 'Payment')}: ${order.paymentMethod || 'COD'}`];
        }
        const normalizedStatus = String(order.orderStatus || "").toLowerCase();
        const isConfirmed = normalizedStatus === "confirmed" || normalizedStatus === "approved";
        const needsAttention = !isConfirmed && !isOtpVerified;
        return {
          ...order,
          risk_level: riskLevel,
          risk_score: riskScore,
          recommended_action: recommendedAction,
          risk_reasons: riskReasons,
          needs_attention: needsAttention,
          is_otp_verified: isOtpVerified,
        };
      }).filter(order => order !== null);
      const riskyOrders = processedOrders.filter(order => order.needs_attention);
      setOrders(riskyOrders);
    } catch (err) {
      console.error("Failed to fetch orders:", err);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [bussinessUuid]);

  // Filtered orders (unchanged)
  const filteredOrders = useMemo(() => {
    if (!Array.isArray(orders)) return [];
    if (!selectedCategory) return [];
    return orders.filter((order) => {
      if (selectedCategory === "low") return order.risk_level === "low";
      if (selectedCategory === "acceptable") return order.risk_level === "acceptable";
      if (selectedCategory === "medium") return order.risk_level === "medium";
      if (selectedCategory === "high") return order.risk_level === "high";
      if (selectedCategory === "very_high") return order.risk_level === "very_high";
      if (selectedCategory === "critical") return order.risk_level === "critical";
      return false;
    });
  }, [orders, selectedCategory]);

  const handleCategoryClick = (categoryId) => {
    setSelectedCategory(categoryId);
    setIsTableOpen(true);
  };

  const handleCloseTable = () => {
    setIsTableOpen(false);
    setSelectedCategory(null);
  };

  const handleAddNote = (order) => {
    setSelectedOrderForNote(order);
    setNoteText(orderNotes[order.orderId] || "");
    setShowNoteModal(true);
  };

  const handleSaveNote = () => {
    if (selectedOrderForNote) {
      setOrderNotes(prev => ({
        ...prev,
        [selectedOrderForNote.orderId]: noteText,
      }));
    }
    setShowNoteModal(false);
    setSelectedOrderForNote(null);
    setNoteText("");
    toast.success(t('RISK_MANAGEMENT.NOTE_SAVED', 'Note saved successfully'));
  };

  const visibleOrders = useMemo(() => {
    if (!Array.isArray(orders)) return [];
    return orders.filter((order) => {
      const normalizedStatus = String(order.orderStatus || "").toLowerCase();
      const isConfirmed = normalizedStatus === "confirmed" || normalizedStatus === "approved";
      const isOtpVerified = order.is_otp_verified === true || 
                            order.orderStatus === "Verified" ||
                            order.riskOtpVerified === true;
      return !isConfirmed && !isOtpVerified;
    });
  }, [orders]);

  const riskSummary = useMemo(() => {
    const summary = { low: 0, acceptable: 0, medium: 0, high: 0, very_high: 0, critical: 0 };
    visibleOrders.forEach((order) => {
      const level = order.risk_level;
      if (summary[level] !== undefined) summary[level] += 1;
    });
    return summary;
  }, [visibleOrders]);

  const categoryCounts = useMemo(() => {
    return {
      low: visibleOrders.filter(o => o.risk_level === "low").length,
      acceptable: visibleOrders.filter(o => o.risk_level === "acceptable").length,
      medium: visibleOrders.filter(o => o.risk_level === "medium").length,
      high: visibleOrders.filter(o => o.risk_level === "high").length,
      very_high: visibleOrders.filter(o => o.risk_level === "very_high").length,
      critical: visibleOrders.filter(o => o.risk_level === "critical").length,
    };
  }, [visibleOrders]);

  const getActionLabelForLevel = (level) => {
    const settingKey = {
      low: "lowRisk",
      acceptable: "acceptableRisk",
      medium: "mediumRisk",
      high: "highRisk",
      very_high: "veryHighRisk",
      critical: "criticalRisk",
    }[level];
    const actionMethod = settingsForm[settingKey]?.approvalMethod;
    return actionLabels[actionMethod]?.label || actionMethod || "—";
  };

  if (loading) {
    return <LogoLoading />;
  }

  return (
    <div className={`min-h-screen w-full ${isDarkMode ? "bg-slate-950 text-slate-100" : "bg-slate-50 text-slate-900"}`}>
      {/* Note Modal - unchanged */}
      {showNoteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className={`w-full max-w-md rounded-2xl shadow-2xl ${isDarkMode ? "bg-slate-900" : "bg-white"}`}>
            <div className={`flex items-center justify-between border-b px-6 py-4 ${isDarkMode ? "border-slate-700" : "border-slate-200"}`}>
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <MdNote className="text-slate-500" />
                {t('RISK_MANAGEMENT.ADD_REVIEW_NOTE', 'Add Review Note')}
              </h2>
              <button
                onClick={() => setShowNoteModal(false)}
                className={`rounded-lg p-2 transition-colors ${isDarkMode ? "hover:bg-slate-800" : "hover:bg-slate-100"}`}
              >
                <MdClose size={20} />
              </button>
            </div>
            <div className="p-6">
              <p className={`text-sm mb-3 ${isDarkMode ? "text-slate-400" : "text-slate-600"}`}>
                {t('RISK_MANAGEMENT.ORDER_NUMBER_LABEL', { 
                  orderId: selectedOrderForNote?.orderId,
                  defaultValue: `Order #${selectedOrderForNote?.orderId}`
                })} - {selectedOrderForNote?.customerName}
              </p>
              <textarea
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder={t('RISK_MANAGEMENT.ENTER_REVIEW_NOTES', 'Enter review notes for this order...')}
                rows={4}
                className={`w-full rounded-lg border px-3 py-2 resize-none ${isDarkMode ? "border-slate-700 bg-slate-800 text-slate-100" : "border-slate-300 bg-white text-slate-800"}`}
              />
            </div>
            <div className={`flex justify-end gap-3 border-t px-6 py-4 ${isDarkMode ? "border-slate-700" : "border-slate-200"}`}>
              <button
                onClick={() => setShowNoteModal(false)}
                className={`px-4 py-2 rounded-lg ${isDarkMode ? "hover:bg-slate-800" : "hover:bg-slate-100"}`}
              >
                {t('RISK_MANAGEMENT.CANCEL', 'Cancel')}
              </button>
              <button
                onClick={handleSaveNote}
                className="px-4 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-800"
              >
                {t('RISK_MANAGEMENT.SAVE_NOTE', 'Save Note')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal - unchanged */}
      {showSettings && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className={`w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl ${isDarkMode ? "bg-slate-900" : "bg-white"}`}>
            <div className={`flex items-center justify-between border-b px-6 py-4 sticky top-0 ${isDarkMode ? "border-slate-700 bg-slate-900" : "border-slate-200 bg-white"}`}>
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <MdSettings className="text-slate-500" />
                {t('RISK_MANAGEMENT.SETTINGS', 'Risk Control Settings')}
              </h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={resetSettings}
                  disabled={savingSettings}
                  className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors ${isDarkMode ? "hover:bg-slate-800" : "hover:bg-slate-100"}`}
                >
                  <MdRefresh size={16} />
                  {t('RISK_MANAGEMENT.RESET', 'Reset')}
                </button>
                <button
                  onClick={() => setShowSettings(false)}
                  className={`rounded-lg p-2 transition-colors ${isDarkMode ? "hover:bg-slate-800" : "hover:bg-slate-100"}`}
                >
                  <MdClose size={20} />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-6">
              {/* Amount Limits */}
              <div className={`p-4 rounded-xl border ${isDarkMode ? "border-slate-700 bg-slate-800/50" : "border-slate-200 bg-slate-50"}`}>
                <h3 className="font-medium mb-3 flex items-center gap-2">
                  <MdAttachMoney className="text-green-500" />
                  {t('RISK_MANAGEMENT.AMOUNT_LIMITS', 'Amount Limits')}
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm mb-1 ${isDarkMode ? "text-slate-400" : "text-slate-600"}`}>
                      {t('RISK_MANAGEMENT.COD_LIMIT_LABEL', 'COD Limit')} (₹)
                    </label>
                    <input
                      type="number"
                      value={settingsForm.codLimit}
                      onChange={(e) => setSettingsForm(prev => ({ ...prev, codLimit: parseInt(e.target.value) || 0 }))}
                      className={`w-full rounded-lg border px-3 py-2 ${isDarkMode ? "border-slate-700 bg-slate-800 text-slate-100" : "border-slate-300 bg-white"}`}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm mb-1 ${isDarkMode ? "text-slate-400" : "text-slate-600"}`}>
                      {t('RISK_MANAGEMENT.PREPAID_LIMIT_LABEL', 'Prepaid Limit')} (₹)
                    </label>
                    <input
                      type="number"
                      value={settingsForm.prepaidLimit}
                      onChange={(e) => setSettingsForm(prev => ({ ...prev, prepaidLimit: parseInt(e.target.value) || 0 }))}
                      className={`w-full rounded-lg border px-3 py-2 ${isDarkMode ? "border-slate-700 bg-slate-800 text-slate-100" : "border-slate-300 bg-white"}`}
                    />
                  </div>
                </div>
              </div>

              {/* Off Hours */}
              <div className={`p-4 rounded-xl border ${isDarkMode ? "border-slate-700 bg-slate-800/50" : "border-slate-200 bg-slate-50"}`}>
                <h3 className="font-medium mb-3 flex items-center gap-2">
                  <MdAccessTime className="text-blue-500" />
                  {t('RISK_MANAGEMENT.OFF_HOURS_PERIOD', 'Off Hours (Higher Risk Period)')}
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm mb-1 ${isDarkMode ? "text-slate-400" : "text-slate-600"}`}>
                      {t('RISK_MANAGEMENT.START_TIME', 'Start Time')}
                    </label>
                    <input
                      type="time"
                      value={settingsForm.offTimeStart}
                      onChange={(e) => setSettingsForm(prev => ({ ...prev, offTimeStart: e.target.value }))}
                      className={`w-full rounded-lg border px-3 py-2 ${isDarkMode ? "border-slate-700 bg-slate-800 text-slate-100" : "border-slate-300 bg-white"}`}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm mb-1 ${isDarkMode ? "text-slate-400" : "text-slate-600"}`}>
                      {t('RISK_MANAGEMENT.END_TIME', 'End Time')}
                    </label>
                    <input
                      type="time"
                      value={settingsForm.offTimeEnd}
                      onChange={(e) => setSettingsForm(prev => ({ ...prev, offTimeEnd: e.target.value }))}
                      className={`w-full rounded-lg border px-3 py-2 ${isDarkMode ? "border-slate-700 bg-slate-800 text-slate-100" : "border-slate-300 bg-white"}`}
                    />
                  </div>
                </div>
              </div>

              {/* Device Settings */}
              <div className={`p-4 rounded-xl border ${isDarkMode ? "border-slate-700 bg-slate-800/50" : "border-slate-200 bg-slate-50"}`}>
                <h3 className="font-medium mb-3 flex items-center gap-2">
                  <MdDevices className="text-purple-500" />
                  {t('RISK_MANAGEMENT.DEVICE_SETTINGS', 'Device Settings')}
                </h3>
                <div>
                  <label className={`block text-sm mb-1 ${isDarkMode ? "text-slate-400" : "text-slate-600"}`}>
                    {t('RISK_MANAGEMENT.MAX_ORDERS_PER_DEVICE_LABEL', 'Max Orders Per Device (within 60 min)')}
                  </label>
                  <input
                    type="number"
                    value={settingsForm.maxOrdersPerDevice}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '') {
                        setSettingsForm(prev => ({ ...prev, maxOrdersPerDevice: '' }));
                        return;
                      }
                      const numValue = parseInt(value);
                      if (!isNaN(numValue) && numValue >= 0) {
                        setSettingsForm(prev => ({ ...prev, maxOrdersPerDevice: numValue }));
                      }
                    }}
                    className={`w-32 rounded-lg border px-3 py-2 ${isDarkMode ? "border-slate-700 bg-slate-800 text-slate-100" : "border-slate-300 bg-white"}`}
                  />
                </div>
              </div>

              {/* Pincode Block List */}
              <div className={`p-4 rounded-xl border ${isDarkMode ? "border-slate-700 bg-slate-800/50" : "border-slate-200 bg-slate-50"}`}>
                <h3 className="font-medium mb-3 flex items-center gap-2">
                  <MdLocationOn className="text-red-500" />
                  {t('RISK_MANAGEMENT.HIGH_RISK_PINCODES', 'High Risk Pincodes')}
                </h3>
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    placeholder={t('RISK_MANAGEMENT.ENTER_PINCODE', 'Enter 6-digit pincode')}
                    maxLength={6}
                    id="pincodeInput"
                    className={`flex-1 rounded-lg border px-3 py-2 ${isDarkMode ? "border-slate-700 bg-slate-800 text-slate-100" : "border-slate-300 bg-white"}`}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        addPincode(e.target.value);
                        e.target.value = '';
                      }
                    }}
                  />
                  <button
                    onClick={() => {
                      const input = document.getElementById('pincodeInput');
                      if (input) {
                        addPincode(input.value);
                        input.value = '';
                      }
                    }}
                    className={`px-4 py-2 rounded-lg ${isDarkMode ? "bg-slate-700 hover:bg-slate-600" : "bg-slate-200 hover:bg-slate-300"}`}
                  >
                    {t('RISK_MANAGEMENT.ADD', 'Add')}
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {settingsForm.pincodeBlockList?.map((pincode) => (
                    <span
                      key={pincode}
                      className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm ${isDarkMode ? "bg-red-900/50 text-red-300" : "bg-red-100 text-red-700"}`}
                    >
                      {pincode}
                      <button onClick={() => removePincode(pincode)} className="hover:text-red-500">
                        <MdClose size={14} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Global Settings */}
              <div className={`p-4 rounded-xl border ${isDarkMode ? "border-slate-700 bg-slate-800/50" : "border-slate-200 bg-slate-50"}`}>
                <h3 className="font-medium mb-3 flex items-center gap-2">
                  <MdInfo className="text-blue-500" />
                  {t('RISK_MANAGEMENT.GLOBAL_SETTINGS', 'Global Settings')}
                </h3>
                <div className="space-y-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={settingsForm.autoApproveLowRisk}
                      onChange={(e) => setSettingsForm(prev => ({ ...prev, autoApproveLowRisk: e.target.checked }))}
                      className="rounded"
                    />
                    <span className="text-sm">{t('RISK_MANAGEMENT.AUTO_APPROVE_LOW_RISK', 'Auto-approve low risk orders')}</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={settingsForm.notifyOnHighRisk}
                      onChange={(e) => setSettingsForm(prev => ({ ...prev, notifyOnHighRisk: e.target.checked }))}
                      className="rounded"
                    />
                    <span className="text-sm">{t('RISK_MANAGEMENT.SEND_NOTIFICATION_HIGH_RISK', 'Send notification for high risk orders')}</span>
                  </label>
                </div>
              </div>

              {/* Risk Level Controls - All 6 levels matching Flask */}
              <div className={`grid gap-4 grid-cols-1 lg:grid-cols-2`}>
                {/* Low Risk Settings */}
                <div className={`rounded-xl border p-4 ${isDarkMode ? "border-green-800 bg-green-950/20" : "border-green-200 bg-green-50/50"}`}>
                  <h3 className={`font-semibold flex items-center gap-2 ${isDarkMode ? "text-green-300" : "text-green-800"}`}>
                    <MdCheckCircle size={18} />
                    {t('RISK_MANAGEMENT.LOW_RISK_SCORE', 'LOW Risk (Score: 0-24)')}
                  </h3>
                  <p className={`text-xs mt-1 mb-3 ${isDarkMode ? "text-green-400/70" : "text-green-600"}`}>
                    {t('RISK_MANAGEMENT.ACTION_APPROVE', 'Action: Approve')}
                  </p>
                  <div className="space-y-3">
                    <label className={`flex items-center gap-2 text-sm ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded accent-green-600"
                        checked={Boolean(settingsForm.lowRisk?.disableCod)}
                        onChange={(e) => updateRiskSetting("lowRisk", "disableCod", e.target.checked)}
                      />
                      {t('RISK_MANAGEMENT.DISABLE_COD', 'Disable Cash on Delivery')}
                    </label>
                    <label className={`flex items-center gap-2 text-sm ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded accent-green-600"
                        checked={Boolean(settingsForm.lowRisk?.disableReturns)}
                        onChange={(e) => updateRiskSetting("lowRisk", "disableReturns", e.target.checked)}
                      />
                      {t('RISK_MANAGEMENT.DISABLE_RETURNS', 'Disable Returns')}
                    </label>
                    <label className={`flex items-center gap-2 text-sm ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded accent-green-600"
                        checked={Boolean(settingsForm.lowRisk?.blockCheckout)}
                        onChange={(e) => updateRiskSetting("lowRisk", "blockCheckout", e.target.checked)}
                      />
                      {t('RISK_MANAGEMENT.BLOCK_CHECKOUT', 'Block Checkout')}
                    </label>
                    <div className="pt-2">
                      <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>
                        {t('RISK_MANAGEMENT.OVERRIDE_ACTION', 'Override Action')}
                      </label>
                      <select
                        className={`w-full rounded-lg border px-3 py-2 text-sm ${isDarkMode ? "border-slate-600 bg-slate-800 text-slate-100" : "border-slate-300 bg-white text-slate-800"}`}
                        value={settingsForm.lowRisk?.approvalMethod || "approve"}
                        onChange={(e) => updateRiskSetting("lowRisk", "approvalMethod", e.target.value)}
                      >
                        <option value="approve">{t('RISK_MANAGEMENT.APPROVE', 'Approve')} {t('RISK_MANAGEMENT.DEFAULT', '(Default)')}</option>
                        <option value="approve_with_log">{t('RISK_MANAGEMENT.APPROVE_WITH_LOG', 'Approve + Monitor')}</option>
                        <option value="otp_verify">{t('RISK_MANAGEMENT.OTP_VERIFY', 'OTP Verify')}</option>
                        <option value="manual_review">{t('RISK_MANAGEMENT.MANUAL_REVIEW', 'Manual Review')}</option>
                        <option value="block">{t('RISK_MANAGEMENT.BLOCK', 'Block')}</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Acceptable Risk Settings */}
                <div className={`rounded-xl border p-4 ${isDarkMode ? "border-blue-800 bg-blue-950/20" : "border-blue-200 bg-blue-50/50"}`}>
                  <h3 className={`font-semibold flex items-center gap-2 ${isDarkMode ? "text-blue-300" : "text-blue-800"}`}>
                    <MdVerifiedUser size={18} />
                    {t('RISK_MANAGEMENT.ACCEPTABLE_RISK_SCORE', 'ACCEPTABLE Risk (Score: 25-44)')}
                  </h3>
                  <p className={`text-xs mt-1 mb-3 ${isDarkMode ? "text-blue-400/70" : "text-blue-600"}`}>
                    {t('RISK_MANAGEMENT.ACTION_APPROVE_WITH_LOG', 'Action: Approve with Log')}
                  </p>
                  <div className="space-y-3">
                    <label className={`flex items-center gap-2 text-sm ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded accent-blue-600"
                        checked={Boolean(settingsForm.acceptableRisk?.disableCod)}
                        onChange={(e) => updateRiskSetting("acceptableRisk", "disableCod", e.target.checked)}
                      />
                      {t('RISK_MANAGEMENT.DISABLE_COD', 'Disable Cash on Delivery')}
                    </label>
                    <label className={`flex items-center gap-2 text-sm ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded accent-blue-600"
                        checked={Boolean(settingsForm.acceptableRisk?.disableReturns)}
                        onChange={(e) => updateRiskSetting("acceptableRisk", "disableReturns", e.target.checked)}
                      />
                      {t('RISK_MANAGEMENT.DISABLE_RETURNS', 'Disable Returns')}
                    </label>
                    <label className={`flex items-center gap-2 text-sm ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded accent-blue-600"
                        checked={Boolean(settingsForm.acceptableRisk?.blockCheckout)}
                        onChange={(e) => updateRiskSetting("acceptableRisk", "blockCheckout", e.target.checked)}
                      />
                      {t('RISK_MANAGEMENT.BLOCK_CHECKOUT', 'Block Checkout')}
                    </label>
                    <div className="pt-2">
                      <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>
                        {t('RISK_MANAGEMENT.OVERRIDE_ACTION', 'Override Action')}
                      </label>
                      <select
                        className={`w-full rounded-lg border px-3 py-2 text-sm ${isDarkMode ? "border-slate-600 bg-slate-800 text-slate-100" : "border-slate-300 bg-white text-slate-800"}`}
                        value={settingsForm.acceptableRisk?.approvalMethod || "approve_with_log"}
                        onChange={(e) => updateRiskSetting("acceptableRisk", "approvalMethod", e.target.value)}
                      >
                        <option value="approve">{t('RISK_MANAGEMENT.APPROVE', 'Approve')}</option>
                        <option value="approve_with_log">{t('RISK_MANAGEMENT.APPROVE_WITH_LOG', 'Approve + Monitor')} {t('RISK_MANAGEMENT.DEFAULT', '(Default)')}</option>
                        <option value="otp_verify">{t('RISK_MANAGEMENT.OTP_VERIFY', 'OTP Verify')}</option>
                        <option value="manual_review">{t('RISK_MANAGEMENT.MANUAL_REVIEW', 'Manual Review')}</option>
                        <option value="block">{t('RISK_MANAGEMENT.BLOCK', 'Block')}</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Medium Risk Settings */}
                <div className={`rounded-xl border p-4 ${isDarkMode ? "border-amber-800 bg-amber-950/20" : "border-amber-200 bg-amber-50/50"}`}>
                  <h3 className={`font-semibold flex items-center gap-2 ${isDarkMode ? "text-amber-300" : "text-amber-800"}`}>
                    <MdWarning size={18} />
                    {t('RISK_MANAGEMENT.MEDIUM_RISK_SCORE', 'MEDIUM Risk (Score: 45-59)')}
                  </h3>
                  <p className={`text-xs mt-1 mb-3 ${isDarkMode ? "text-amber-400/70" : "text-amber-600"}`}>
                    {t('RISK_MANAGEMENT.ACTION_OTP_VERIFY', 'Action: OTP Verify')}
                  </p>
                  <div className="space-y-3">
                    <label className={`flex items-center gap-2 text-sm ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded accent-amber-600"
                        checked={Boolean(settingsForm.mediumRisk?.disableCod)}
                        onChange={(e) => updateRiskSetting("mediumRisk", "disableCod", e.target.checked)}
                      />
                      {t('RISK_MANAGEMENT.DISABLE_COD', 'Disable Cash on Delivery')}
                    </label>
                    <label className={`flex items-center gap-2 text-sm ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded accent-amber-600"
                        checked={Boolean(settingsForm.mediumRisk?.disableReturns)}
                        onChange={(e) => updateRiskSetting("mediumRisk", "disableReturns", e.target.checked)}
                      />
                      {t('RISK_MANAGEMENT.DISABLE_RETURNS', 'Disable Returns')}
                    </label>
                    <label className={`flex items-center gap-2 text-sm ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded accent-amber-600"
                        checked={Boolean(settingsForm.mediumRisk?.blockCheckout)}
                        onChange={(e) => updateRiskSetting("mediumRisk", "blockCheckout", e.target.checked)}
                      />
                      {t('RISK_MANAGEMENT.BLOCK_CHECKOUT', 'Block Checkout')}
                    </label>
                    <div className="pt-2">
                      <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>
                        {t('RISK_MANAGEMENT.OVERRIDE_ACTION', 'Override Action')}
                      </label>
                      <select
                        className={`w-full rounded-lg border px-3 py-2 text-sm ${isDarkMode ? "border-slate-600 bg-slate-800 text-slate-100" : "border-slate-300 bg-white text-slate-800"}`}
                        value={settingsForm.mediumRisk?.approvalMethod || "otp_verify"}
                        onChange={(e) => updateRiskSetting("mediumRisk", "approvalMethod", e.target.value)}
                      >
                        <option value="approve">{t('RISK_MANAGEMENT.APPROVE', 'Approve')}</option>
                        <option value="approve_with_log">{t('RISK_MANAGEMENT.APPROVE_WITH_LOG', 'Approve + Monitor')}</option>
                        <option value="otp_verify">{t('RISK_MANAGEMENT.OTP_VERIFY', 'OTP Verify')} {t('RISK_MANAGEMENT.DEFAULT', '(Default)')}</option>
                        <option value="manual_review">{t('RISK_MANAGEMENT.MANUAL_REVIEW', 'Manual Review')}</option>
                        <option value="block">{t('RISK_MANAGEMENT.BLOCK', 'Block')}</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* High Risk Settings */}
                <div className={`rounded-xl border p-4 ${isDarkMode ? "border-orange-800 bg-orange-950/20" : "border-orange-200 bg-orange-50/50"}`}>
                  <h3 className={`font-semibold flex items-center gap-2 ${isDarkMode ? "text-orange-300" : "text-orange-800"}`}>
                    <MdWarning size={18} />
                    {t('RISK_MANAGEMENT.HIGH_RISK_SCORE', 'HIGH Risk (Score: 60-74)')}
                  </h3>
                  <p className={`text-xs mt-1 mb-3 ${isDarkMode ? "text-orange-400/70" : "text-orange-600"}`}>
                    {t('RISK_MANAGEMENT.ACTION_MANUAL_REVIEW', 'Action: Manual Review')}
                  </p>
                  <div className="space-y-3">
                    <label className={`flex items-center gap-2 text-sm ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded accent-orange-600"
                        checked={Boolean(settingsForm.highRisk?.disableCod)}
                        onChange={(e) => updateRiskSetting("highRisk", "disableCod", e.target.checked)}
                      />
                      {t('RISK_MANAGEMENT.DISABLE_COD', 'Disable Cash on Delivery')}
                    </label>
                    <label className={`flex items-center gap-2 text-sm ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded accent-orange-600"
                        checked={Boolean(settingsForm.highRisk?.disableReturns)}
                        onChange={(e) => updateRiskSetting("highRisk", "disableReturns", e.target.checked)}
                      />
                      {t('RISK_MANAGEMENT.DISABLE_RETURNS', 'Disable Returns')}
                    </label>
                    <label className={`flex items-center gap-2 text-sm ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded accent-orange-600"
                        checked={Boolean(settingsForm.highRisk?.blockCheckout)}
                        onChange={(e) => updateRiskSetting("highRisk", "blockCheckout", e.target.checked)}
                      />
                      {t('RISK_MANAGEMENT.BLOCK_CHECKOUT', 'Block Checkout')}
                    </label>
                    <div className="pt-2">
                      <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>
                        {t('RISK_MANAGEMENT.OVERRIDE_ACTION', 'Override Action')}
                      </label>
                      <select
                        className={`w-full rounded-lg border px-3 py-2 text-sm ${isDarkMode ? "border-slate-600 bg-slate-800 text-slate-100" : "border-slate-300 bg-white text-slate-800"}`}
                        value={settingsForm.highRisk?.approvalMethod || "manual_review"}
                        onChange={(e) => updateRiskSetting("highRisk", "approvalMethod", e.target.value)}
                      >
                        <option value="approve">{t('RISK_MANAGEMENT.APPROVE', 'Approve')}</option>
                        <option value="approve_with_log">{t('RISK_MANAGEMENT.APPROVE_WITH_LOG', 'Approve + Monitor')}</option>
                        <option value="otp_verify">{t('RISK_MANAGEMENT.OTP_VERIFY', 'OTP Verify')}</option>
                        <option value="manual_review">{t('RISK_MANAGEMENT.MANUAL_REVIEW', 'Manual Review')} {t('RISK_MANAGEMENT.DEFAULT', '(Default)')}</option>
                        <option value="block">{t('RISK_MANAGEMENT.BLOCK', 'Block')}</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Very High Risk Settings */}
                <div className={`rounded-xl border p-4 ${isDarkMode ? "border-red-800 bg-red-950/30" : "border-red-200 bg-red-50/50"}`}>
                  <h3 className={`font-semibold flex items-center gap-2 ${isDarkMode ? "text-red-300" : "text-red-800"}`}>
                    <MdBlock size={18} />
                    {t('RISK_MANAGEMENT.VERY_HIGH_RISK_SCORE', 'VERY HIGH Risk (Score: 75-89)')}
                  </h3>
                  <p className={`text-xs mt-1 mb-3 ${isDarkMode ? "text-red-400/70" : "text-red-600"}`}>
                    {t('RISK_MANAGEMENT.ACTION_BLOCK', 'Action: Block')}
                  </p>
                  <div className="space-y-3">
                    <label className={`flex items-center gap-2 text-sm ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded accent-red-600"
                        checked={Boolean(settingsForm.veryHighRisk?.disableCod)}
                        onChange={(e) => updateRiskSetting("veryHighRisk", "disableCod", e.target.checked)}
                      />
                      {t('RISK_MANAGEMENT.DISABLE_COD', 'Disable Cash on Delivery')}
                    </label>
                    <label className={`flex items-center gap-2 text-sm ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded accent-red-600"
                        checked={Boolean(settingsForm.veryHighRisk?.disableReturns)}
                        onChange={(e) => updateRiskSetting("veryHighRisk", "disableReturns", e.target.checked)}
                      />
                      {t('RISK_MANAGEMENT.DISABLE_RETURNS', 'Disable Returns')}
                    </label>
                    <label className={`flex items-center gap-2 text-sm ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded accent-red-600"
                        checked={Boolean(settingsForm.veryHighRisk?.blockCheckout)}
                        onChange={(e) => updateRiskSetting("veryHighRisk", "blockCheckout", e.target.checked)}
                      />
                      {t('RISK_MANAGEMENT.BLOCK_CHECKOUT', 'Block Checkout')}
                    </label>
                    <div className="pt-2">
                      <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>
                        {t('RISK_MANAGEMENT.OVERRIDE_ACTION', 'Override Action')}
                      </label>
                      <select
                        className={`w-full rounded-lg border px-3 py-2 text-sm ${isDarkMode ? "border-slate-600 bg-slate-800 text-slate-100" : "border-slate-300 bg-white text-slate-800"}`}
                        value={settingsForm.veryHighRisk?.approvalMethod || "block"}
                        onChange={(e) => updateRiskSetting("veryHighRisk", "approvalMethod", e.target.value)}
                      >
                        <option value="approve">{t('RISK_MANAGEMENT.APPROVE', 'Approve')}</option>
                        <option value="approve_with_log">{t('RISK_MANAGEMENT.APPROVE_WITH_LOG', 'Approve + Monitor')}</option>
                        <option value="otp_verify">{t('RISK_MANAGEMENT.OTP_VERIFY', 'OTP Verify')}</option>
                        <option value="manual_review">{t('RISK_MANAGEMENT.MANUAL_REVIEW', 'Manual Review')}</option>
                        <option value="block">{t('RISK_MANAGEMENT.BLOCK', 'Block')} {t('RISK_MANAGEMENT.DEFAULT', '(Default)')}</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Critical Risk Settings */}
                <div className={`rounded-xl border p-4 ${isDarkMode ? "border-red-900 bg-red-950/50" : "border-red-300 bg-red-100/50"}`}>
                  <h3 className={`font-semibold flex items-center gap-2 ${isDarkMode ? "text-red-200" : "text-red-900"}`}>
                    <MdBlock size={18} />
                    {t('RISK_MANAGEMENT.CRITICAL_RISK_SCORE', 'CRITICAL Risk (Score: 90-100)')}
                  </h3>
                  <p className={`text-xs mt-1 mb-3 ${isDarkMode ? "text-red-400/70" : "text-red-700"}`}>
                    {t('RISK_MANAGEMENT.ACTION_BLOCK', 'Action: Block')}
                  </p>
                  <div className="space-y-3">
                    <label className={`flex items-center gap-2 text-sm ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded accent-red-700"
                        checked={Boolean(settingsForm.criticalRisk?.disableCod)}
                        disabled
                      />
                      {t('RISK_MANAGEMENT.DISABLE_COD', 'Disable Cash on Delivery')} {t('RISK_MANAGEMENT.FORCED', '(Forced)')}
                    </label>
                    <label className={`flex items-center gap-2 text-sm ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded accent-red-700"
                        checked={Boolean(settingsForm.criticalRisk?.disableReturns)}
                        disabled
                      />
                      {t('RISK_MANAGEMENT.DISABLE_RETURNS', 'Disable Returns')} {t('RISK_MANAGEMENT.FORCED', '(Forced)')}
                    </label>
                    <label className={`flex items-center gap-2 text-sm ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded accent-red-700"
                        checked={Boolean(settingsForm.criticalRisk?.blockCheckout)}
                        disabled
                      />
                      {t('RISK_MANAGEMENT.BLOCK_CHECKOUT', 'Block Checkout')} {t('RISK_MANAGEMENT.FORCED', '(Forced)')}
                    </label>
                    <div className="pt-2">
                      <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>
                        {t('RISK_MANAGEMENT.OVERRIDE_ACTION', 'Override Action')} {t('RISK_MANAGEMENT.FORCED', '(Forced)')}
                      </label>
                      <select
                        className={`w-full rounded-lg border px-3 py-2 text-sm ${isDarkMode ? "border-slate-600 bg-slate-800 text-slate-100" : "border-slate-300 bg-white text-slate-800"}`}
                        value={settingsForm.criticalRisk?.approvalMethod || "block"}
                        disabled
                      >
                        <option value="block">{t('RISK_MANAGEMENT.BLOCK', 'Block')} {t('RISK_MANAGEMENT.FORCED', '(Forced)')}</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className={`flex justify-end gap-3 border-t px-6 py-4 sticky bottom-0 ${isDarkMode ? "border-slate-700 bg-slate-900" : "border-slate-200 bg-white"}`}>
              <button
                onClick={() => setShowSettings(false)}
                className={`px-4 py-2 rounded-lg ${isDarkMode ? "hover:bg-slate-800" : "hover:bg-slate-100"}`}
              >
                {t('RISK_MANAGEMENT.CANCEL', 'Cancel')}
              </button>
              <button
                onClick={saveSettings}
                disabled={savingSettings}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
              >
                {savingSettings ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    {t('RISK_MANAGEMENT.SAVING', 'Saving...')}
                  </>
                ) : (
                  <>
                    <MdSave size={16} />
                    {t('RISK_MANAGEMENT.SAVE_SETTINGS', 'Save Settings')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content - Responsive Layout with full height */}
      <div className="flex flex-col lg:flex-row h-screen w-full overflow-hidden">
        {/* Left Side - Main Content */}
        <div className={`
          ${isTableOpen ? 'hidden lg:block lg:w-[400px] xl:w-[450px] flex-shrink-0' : 'w-full'}
          p-4 md:p-6 overflow-y-auto h-full
          transition-all duration-300
        `}>
          {/* Header */}
        {/* Header */}
<div className={`mb-6 rounded-2xl border px-4 md:px-6 py-4 md:py-6 shadow-lg ${
  isDarkMode
    ? "border-slate-700 bg-gradient-to-br from-slate-900 via-slate-900 to-indigo-950 text-white"
    : "border-slate-200 bg-gradient-to-br from-slate-100 via-white to-blue-50 text-slate-800"
}`}>
  <div className="flex flex-wrap items-center justify-between gap-4">
    <div>
      <h1 className="text-xl md:text-2xl font-bold flex items-center gap-2">
        <MdOutlineSecurity className={isDarkMode ? "text-blue-300" : "text-blue-600"} /> 
        {t('RISK_MANAGEMENT.TITLE', 'Risk Management')}
      </h1>
      <p className={`mt-2 text-xs md:text-sm ${isDarkMode ? "text-slate-200" : "text-slate-600"}`}>
        {t('RISK_MANAGEMENT.SUBTITLE', 'Monitor and manage risk based on Flask risk assessment')}
      </p>
    </div>
    <div className={`rounded-xl px-3 md:px-4 py-2 md:py-3 backdrop-blur-sm ${
      isDarkMode ? "bg-white/10" : "bg-blue-50/80"
    }`}>
      <div className={`text-xs uppercase tracking-wide ${isDarkMode ? "text-slate-200" : "text-slate-500"}`}>
        {t('RISK_MANAGEMENT.FLAGGED_ORDERS', 'Flagged Orders')}
      </div>
      <div className={`text-xl md:text-2xl font-semibold ${isDarkMode ? "text-white" : "text-slate-800"}`}>
        {visibleOrders.length}
      </div>
    </div>
  </div>
</div>

          <div className={`mb-6 ${isTableOpen ? "" : ""}`}>
            <h3 className={`text-sm font-semibold mb-3 ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>
              {t('RISK_MANAGEMENT.VIEW_BY_RISK_LEVEL', 'View by Risk Level')}
            </h3>
            <div className={`
              grid gap-3
              ${isTableOpen 
                ? 'grid-cols-1 sm:grid-cols-2' 
                : 'grid-cols-2 md:grid-cols-3 lg:grid-cols-6'
              }
            `}>
              {riskCategories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => handleCategoryClick(category.id)}
                  className={`relative overflow-hidden rounded-xl border p-3 md:p-4 text-left transition-all hover:scale-105 ${
                    selectedCategory === category.id
                      ? "ring-2 ring-offset-2 dark:ring-offset-slate-900"
                      : categoryLightColors[category.id]
                  } ${
                    selectedCategory === category.id
                      ? category.bgColor.replace('bg-', 'ring-')
                      : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 md:gap-3">
                      <span className="text-lg md:text-xl">{category.icon}</span>
                      <div>
                        <div className="text-xs md:text-sm">{category.label}</div>
                        <div className="text-2xl md:text-3xl font-bold">
                          {categoryCounts[category.id]}
                        </div>
                      </div>
                    </div>
                    <div className="text-xs opacity-70 hidden sm:block">
                      {category.id === "low" && "0-24"}
                      {category.id === "acceptable" && "25-44"}
                      {category.id === "medium" && "45-59"}
                      {category.id === "high" && "60-74"}
                      {category.id === "very_high" && "75-89"}
                      {category.id === "critical" && "90-100"}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Active Risk Settings */}
          <div className={`mt-6 rounded-xl border p-4 ${isDarkMode ? "border-slate-700 bg-slate-800/30" : "border-slate-200 bg-slate-50"}`}>
            <div className="flex items-center justify-between mb-3">
              <h3 className={`text-sm font-semibold flex items-center gap-2 ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>
                <MdSettings size={16} />
                {t('RISK_MANAGEMENT.ACTIVE_RISK_SETTINGS', 'Active Risk Settings')}
              </h3>
              <button
                onClick={() => setShowSettings(true)}
                className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs transition-colors bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600"
              >
                <MdEdit size={12} />
                <span className="hidden sm:inline">{t('RISK_MANAGEMENT.CONFIGURE', 'Configure')}</span>
              </button>
            </div>

            <div className="space-y-3">
              {/* Amount & Time Limits Row */}
              <div className={`grid grid-cols-1 sm:grid-cols-3 gap-3 p-3 rounded-lg ${isDarkMode ? "bg-slate-800/50" : "bg-white"}`}>
                <div className="flex items-center gap-2">
                  <MdAttachMoney className="text-green-500 flex-shrink-0" size={14} />
                  <span className="text-xs font-medium">{t('RISK_MANAGEMENT.COD_LIMIT', 'COD')}:</span>
                  <span className={`text-xs font-semibold ${isDarkMode ? "text-green-300" : "text-green-700"}`}>
                    ₹{settingsForm.codLimit?.toLocaleString()}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <MdAttachMoney className="text-blue-500 flex-shrink-0" size={14} />
                  <span className="text-xs font-medium">{t('RISK_MANAGEMENT.PREPAID_LIMIT', 'Prepaid')}:</span>
                  <span className={`text-xs font-semibold ${isDarkMode ? "text-blue-300" : "text-blue-700"}`}>
                    ₹{settingsForm.prepaidLimit?.toLocaleString()}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <MdAccessTime className="text-purple-500 flex-shrink-0" size={14} />
                  <span className="text-xs font-medium">{t('RISK_MANAGEMENT.OFF_HOURS', 'Off')}:</span>
                  <span className={`text-xs font-semibold ${isDarkMode ? "text-purple-300" : "text-purple-700"}`}>
                    {settingsForm.offTimeStart}-{settingsForm.offTimeEnd}
                  </span>
                </div>
              </div>

              {/* Device & Pincode Row */}
              <div className={`grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 rounded-lg ${isDarkMode ? "bg-slate-800/50" : "bg-white"}`}>
                <div className="flex items-center gap-2">
                  <MdDevices className="text-indigo-500 flex-shrink-0" size={14} />
                  <span className="text-xs font-medium">{t('RISK_MANAGEMENT.MAX_PER_DEVICE', 'Max/Device')}:</span>
                  <span className={`text-xs font-semibold ${isDarkMode ? "text-indigo-300" : "text-indigo-700"}`}>
                    {settingsForm.maxOrdersPerDevice}/hr
                  </span>
                </div>
                <div className="flex items-start gap-2 flex-wrap">
                  <MdLocationOn className="text-red-500 mt-0.5 flex-shrink-0" size={14} />
                  <span className="text-xs font-medium">{t('RISK_MANAGEMENT.PINCODES', 'Pincodes')}:</span>
                  {settingsForm.pincodeBlockList?.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {settingsForm.pincodeBlockList.slice(0, 3).map((pincode) => (
                        <span key={pincode} className={`text-xs px-1.5 py-0.5 rounded ${isDarkMode ? "bg-red-900/50 text-red-300" : "bg-red-100 text-red-700"}`}>
                          {pincode}
                        </span>
                      ))}
                      {settingsForm.pincodeBlockList.length > 3 && (
                        <span className="text-xs text-slate-500">+{settingsForm.pincodeBlockList.length - 3}</span>
                      )}
                    </div>
                  ) : (
                    <span className="text-xs text-slate-500">{t('RISK_MANAGEMENT.NONE', 'None')}</span>
                  )}
                </div>
              </div>

              {/* Risk Level Actions */}
              <div className={`p-3 rounded-lg ${isDarkMode ? "bg-slate-800/50" : "bg-white"}`}>
                <div className="text-xs font-medium mb-2 flex items-center gap-2">
                  <MdWarning size={14} className="text-yellow-500" />
                  {t('RISK_MANAGEMENT.RISK_LEVEL_ACTIONS', 'Risk Level Actions')}:
                </div>
                <div className={`grid gap-2 ${isTableOpen ? 'grid-cols-3' : 'grid-cols-3 md:grid-cols-6'}`}>
                  {[
                    { level: t('RISK_MANAGEMENT.LOW', 'Low'), key: 'lowRisk', color: 'text-green-700 dark:text-green-400', bg: 'bg-green-50 dark:bg-green-950/30' },
                    { level: t('RISK_MANAGEMENT.ACCEPTABLE', 'Acc'), key: 'acceptableRisk', color: 'text-blue-700 dark:text-blue-400', bg: 'bg-blue-50 dark:bg-blue-950/30' },
                    { level: t('RISK_MANAGEMENT.MEDIUM', 'Med'), key: 'mediumRisk', color: 'text-yellow-700 dark:text-yellow-400', bg: 'bg-yellow-50 dark:bg-yellow-950/30' },
                    { level: t('RISK_MANAGEMENT.HIGH', 'High'), key: 'highRisk', color: 'text-orange-700 dark:text-orange-400', bg: 'bg-orange-50 dark:bg-orange-950/30' },
                    { level: t('RISK_MANAGEMENT.VERY_HIGH', 'V.High'), key: 'veryHighRisk', color: 'text-red-700 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-950/30' },
                    { level: t('RISK_MANAGEMENT.CRITICAL', 'Crit'), key: 'criticalRisk', color: 'text-red-800 dark:text-red-300', bg: 'bg-red-100 dark:bg-red-950/50' },
                  ].map(({ level, key, color, bg }) => (
                    <div key={level} className={`flex flex-col items-center p-2 rounded ${bg}`}>
                      <span className={`text-xs font-bold ${color}`}>{level}</span>
                      <span className={`text-[10px] ${color.replace('700', '600').replace('800', '700').replace('400', '500').replace('300', '400')}`}>
                        {actionLabels[settingsForm[key]?.approvalMethod]?.label || "—"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Additional Settings Summary */}
              <div className={`grid grid-cols-1 sm:grid-cols-2 gap-2 p-3 rounded-lg ${isDarkMode ? "bg-slate-800/50" : "bg-white"} text-xs`}>
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${settingsForm.autoApproveLowRisk ? "bg-green-500" : "bg-red-500"}`}></div>
                  <span className="font-medium">{t('RISK_MANAGEMENT.AUTO_APPROVE', 'Auto-approve')}:</span>
                  <span className={settingsForm.autoApproveLowRisk ? "text-green-500" : "text-red-500"}>
                    {settingsForm.autoApproveLowRisk ? t('RISK_MANAGEMENT.ON', 'ON') : t('RISK_MANAGEMENT.OFF', 'OFF')}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${settingsForm.notifyOnHighRisk ? "bg-green-500" : "bg-red-500"}`}></div>
                  <span className="font-medium">{t('RISK_MANAGEMENT.NOTIFY', 'Notify')}:</span>
                  <span className={settingsForm.notifyOnHighRisk ? "text-green-500" : "text-red-500"}>
                    {settingsForm.notifyOnHighRisk ? t('RISK_MANAGEMENT.ON', 'ON') : t('RISK_MANAGEMENT.OFF', 'OFF')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Table Panel (Full screen on mobile when open) */}
        {isTableOpen && (
          <div className={`
            ${isMobileView ? 'fixed inset-0 z-50' : 'flex-1'}
            border-l overflow-hidden flex flex-col 
            ${isDarkMode ? "border-slate-700 bg-slate-900" : "border-slate-200 bg-white"}
          `}>
            {/* Table Header */}
            <div className={`flex items-center justify-between px-4 md:px-6 py-3 md:py-4 border-b ${isDarkMode ? "border-slate-700" : "border-slate-200"}`}>
              <div className="flex items-center gap-2 md:gap-3">
                <button
                  onClick={handleCloseTable}
                  className={`lg:hidden p-2 rounded-lg transition-colors ${isDarkMode ? "hover:bg-slate-800" : "hover:bg-slate-100"}`}
                >
                  <MdArrowBack size={20} />
                </button>
                <div className={`p-2 rounded-lg ${selectedCategory && riskCategories.find(c => c.id === selectedCategory)?.bgColor}`}>
                  <span className="text-white">{selectedCategory && riskCategories.find(c => c.id === selectedCategory)?.icon}</span>
                </div>
                <div>
                  <h2 className={`text-base md:text-lg font-semibold ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>
                    {selectedCategory && riskCategories.find(c => c.id === selectedCategory)?.label} {t('RISK_MANAGEMENT.ORDERS', 'Orders')}
                  </h2>
                  <p className={`text-xs md:text-sm ${isDarkMode ? "text-slate-400" : "text-slate-600"}`}>
                    {filteredOrders.length} {t('RISK_MANAGEMENT.ORDERS_WITH_RISK', 'orders with {{level}} risk level', { level: selectedCategory?.replace('_', ' ') })}
                  </p>
                </div>
              </div>
              <button
                onClick={handleCloseTable}
                className={`hidden lg:block p-2 rounded-lg transition-colors ${isDarkMode ? "hover:bg-slate-800" : "hover:bg-slate-100"}`}
              >
                <MdClose size={20} />
              </button>
            </div>

            {/* Table Content - Desktop Table View */}
            <div className="flex-1 overflow-auto">
              <div className="hidden md:block">
                <table className="w-full">
                  <thead className={`sticky top-0 z-10 ${isDarkMode ? "bg-slate-800" : "bg-slate-100"}`}>
                    <tr>
                      <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>#</th>
                      <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>
                        {t('RISK_MANAGEMENT.ORDER_NUMBER', 'Order #')}
                      </th>
                      <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>
                        {t('RISK_MANAGEMENT.CUSTOMER', 'Customer')}
                      </th>
                      <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>
                        {t('RISK_MANAGEMENT.AMOUNT', 'Amount')}
                      </th>
                      <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>
                        {t('RISK_MANAGEMENT.PAYMENT', 'Payment')}
                      </th>
                      <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>
                        {t('RISK_MANAGEMENT.RISK', 'Risk')}
                      </th>
                      <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>
                        {t('RISK_MANAGEMENT.REASONS', 'Reasons')}
                      </th>
                      <th className={`px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>
                        {t('RISK_MANAGEMENT.STATUS', 'Status')}
                      </th>
                      <th className={`px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>
                        {t('RISK_MANAGEMENT.ACTION', 'Action')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className={isDarkMode ? "divide-y divide-slate-700" : "divide-y divide-slate-100"}>
                    {filteredOrders.length === 0 ? (
                      <tr>
                        <td colSpan={9} className={`py-10 text-center text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                          {t('RISK_MANAGEMENT.NO_ORDERS_FOUND', 'No orders found with {{level}} risk level', { level: selectedCategory?.replace('_', ' ') })}
                        </td>
                      </tr>
                    ) : (
                      filteredOrders.map((order, index) => {
                        const hasNote = orderNotes[order.orderId];
                        const isManualReview = order.recommended_action === "manual_review";
                        const reasons = order.risk_reasons || [];
                        const isConfirmed = order.orderStatus === "Confirmed" || order.orderStatus === "Approved";
                        const formatOrderDate = (dateString) => {
                          if (!dateString) return null;
                          const date = new Date(dateString);
                          return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
                        };
                        const orderDate = formatOrderDate(order.createdAt || order.orderDate || order.created_at);
                        return (
                          <tr key={order.orderId} className={isDarkMode ? "hover:bg-slate-800/60" : "hover:bg-slate-50"}>
                            <td className={`px-4 py-3 text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                              {index + 1}
                            </td>
                            <td className="px-4 py-3">
                              <div className="font-mono text-sm font-medium">
                                #{order.orderId}
                              </div>
                              {orderDate && (
                                <div className={`text-xs mt-0.5 ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>
                                  {orderDate}
                                </div>
                              )}
                            </td>
                            <td className={`px-4 py-3 text-sm ${isDarkMode ? "text-slate-100" : "text-slate-800"}`}>
                              {order.customerName || t('RISK_MANAGEMENT.UNKNOWN', 'Unknown')}
                            </td>
                            <td className={`px-4 py-3 text-sm font-medium ${isDarkMode ? "text-slate-100" : "text-slate-800"}`}>
                              ₹{order.total?.toFixed ? order.total.toFixed(2) : order.total}
                            </td>
                            <td className={`px-4 py-3 text-sm ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>
                              {order.paymentMethod || "—"}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${riskLevelColors[order.risk_level] || "bg-gray-200 text-gray-800"}`}>
                                {order.risk_level?.toUpperCase() || "—"}
                              </span>
                            </td>
                            <td className={`px-4 py-3 text-sm ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>
                              <div className="max-w-[200px]">
                                {reasons.length > 0 ? (
                                  <ul className="list-disc list-inside space-y-0.5 text-xs">
                                    {reasons.slice(0, 2).map((reason, i) => (
                                      <li key={i} className="break-words">{reason}</li>
                                    ))}
                                    {reasons.length > 2 && <li className="text-slate-500">{t('RISK_MANAGEMENT.MORE', '+{{count}} more', { count: reasons.length - 2 })}</li>}
                                  </ul>
                                ) : (
                                  <span className="text-slate-500">—</span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-center">
                              {isConfirmed ? (
                                <span className="inline-flex items-center gap-1 text-green-500 text-xs">
                                  <MdCheckCircle size={14} />
                                  {t('RISK_MANAGEMENT.CONFIRMED', 'Confirmed')}
                                </span>
                              ) : (
                                <span className={`inline-flex items-center gap-1 text-xs ${isDarkMode ? "text-yellow-400" : "text-yellow-600"}`}>
                                  <MdWarning size={14} />
                                  {order.orderStatus || t('RISK_MANAGEMENT.PENDING', 'Pending')}
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <div className="flex items-center justify-center gap-2">
                                {!isConfirmed && (
                                  <button
                                    onClick={() => handleConfirmOrder(order)}
                                    disabled={updatingOrderId === order.orderId}
                                    className={`p-1.5 rounded-lg transition-colors ${
                                      isDarkMode 
                                        ? "bg-green-900/50 hover:bg-green-800 text-green-300" 
                                        : "bg-green-100 hover:bg-green-200 text-green-700"
                                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                                    title={t('RISK_MANAGEMENT.CONFIRM_ORDER', 'Confirm Order')}
                                  >
                                    {updatingOrderId === order.orderId ? (
                                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-green-500 border-t-transparent"></div>
                                    ) : (
                                      <MdCheck size={16} />
                                    )}
                                  </button>
                                )}
                                {isManualReview && !isConfirmed && (
                                  <button
                                    onClick={() => handleAddNote(order)}
                                    className={`p-1.5 rounded-lg transition-colors ${isDarkMode ? "hover:bg-slate-700 bg-slate-800" : "hover:bg-slate-100 bg-slate-50"}`}
                                    title={hasNote ? t('RISK_MANAGEMENT.EDIT_NOTE', 'Edit Note') : t('RISK_MANAGEMENT.ADD_NOTE', 'Add Review Note')}
                                  >
                                    {hasNote ? (
                                      <MdNote size={14} className="text-blue-500" />
                                    ) : (
                                      <MdAdd size={14} />
                                    )}
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="md:hidden divide-y">
                {filteredOrders.length === 0 ? (
                  <div className={`py-10 text-center text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                    {t('RISK_MANAGEMENT.NO_ORDERS_FOUND', 'No orders found with {{level}} risk level', { level: selectedCategory?.replace('_', ' ') })}
                  </div>
                ) : (
                  filteredOrders.map((order, index) => {
                    const hasNote = orderNotes[order.orderId];
                    const isManualReview = order.recommended_action === "manual_review";
                    const reasons = order.risk_reasons || [];
                    const isConfirmed = order.orderStatus === "Confirmed" || order.orderStatus === "Approved";
                    const formatOrderDate = (dateString) => {
                      if (!dateString) return null;
                      const date = new Date(dateString);
                      return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
                    };
                    const orderDate = formatOrderDate(order.createdAt || order.orderDate || order.created_at);
                    return (
                      <div key={order.orderId} className={`p-4 space-y-3 ${isDarkMode ? "bg-slate-900" : "bg-white"}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className={`text-xs ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>#{index + 1}</span>
                            <span className="font-mono text-sm font-medium">#{order.orderId}</span>
                            <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${riskLevelColors[order.risk_level] || "bg-gray-200 text-gray-800"}`}>
                              {order.risk_level?.toUpperCase() || "—"}
                            </span>
                          </div>
                          <span className={`text-sm font-medium ${isDarkMode ? "text-slate-100" : "text-slate-800"}`}>
                            ₹{order.total?.toFixed ? order.total.toFixed(2) : order.total}
                          </span>
                        </div>
                        <div className={`flex items-center justify-between text-xs ${isDarkMode ? "text-slate-400" : "text-slate-600"}`}>
                          <div className="flex items-center gap-3">
                            <span className="flex items-center gap-1">
                              <MdPerson size={12} />
                              {order.customerName || t('RISK_MANAGEMENT.UNKNOWN', 'Unknown')}
                            </span>
                            {orderDate && (
                              <span className="flex items-center gap-1">
                                <MdAccessTime size={12} />
                                {orderDate}
                              </span>
                            )}
                          </div>
                          <span>{order.paymentMethod || "—"}</span>
                        </div>
                        {reasons.length > 0 && (
                          <div className={`text-xs ${isDarkMode ? "text-slate-500" : "text-slate-500"}`}>
                            <span className="font-medium">{t('RISK_MANAGEMENT.REASONS', 'Reasons')}: </span>
                            {reasons.slice(0, 2).join(", ")}
                            {reasons.length > 2 && ` ${t('RISK_MANAGEMENT.MORE', '+{{count}} more', { count: reasons.length - 2 })}`}
                          </div>
                        )}
                        <div className="flex items-center justify-between pt-1 border-t border-slate-200 dark:border-slate-700">
                          <span className={`text-xs ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                            {isConfirmed ? (
                              <span className="inline-flex items-center gap-1 text-green-500">
                                <MdCheckCircle size={12} />
                                {t('RISK_MANAGEMENT.CONFIRMED', 'Confirmed')}
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1">
                                <MdWarning size={12} className="text-yellow-500" />
                                {order.orderStatus || t('RISK_MANAGEMENT.PENDING', 'Pending')}
                              </span>
                            )}
                          </span>
                          <div className="flex items-center gap-2">
                            {!isConfirmed && (
                              <button
                                onClick={() => handleConfirmOrder(order)}
                                disabled={updatingOrderId === order.orderId}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                  isDarkMode 
                                    ? "bg-green-900/50 hover:bg-green-800 text-green-300" 
                                    : "bg-green-100 hover:bg-green-200 text-green-700"
                                } disabled:opacity-50`}
                              >
                                {updatingOrderId === order.orderId ? (
                                  <div className="animate-spin rounded-full h-3 w-3 border-2 border-green-500 border-t-transparent mx-2"></div>
                                ) : (
                                  t('RISK_MANAGEMENT.CONFIRM', 'Confirm')
                                )}
                              </button>
                            )}
                            {isManualReview && !isConfirmed && (
                              <button
                                onClick={() => handleAddNote(order)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                  isDarkMode 
                                    ? "bg-slate-800 hover:bg-slate-700 text-slate-300" 
                                    : "bg-slate-100 hover:bg-slate-200 text-slate-700"
                                }`}
                              >
                                {hasNote ? t('RISK_MANAGEMENT.EDIT_NOTE', 'Edit Note') : t('RISK_MANAGEMENT.ADD_NOTE', 'Add Note')}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}